import React, { FC, useState } from "react";
import { ScrollView, StyleSheet, Text, TextInput, View } from "react-native";
import { useSelector } from "react-redux";

import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { useAppDispatch } from "src/redux";
import { kColors } from "src/kColors";
import { kFonts } from "src/kFonts";
import { kStyleConst } from "src/kStyle";
import { loginWithEmailAndPassword, validatePassword } from "src/redux/user/actions";
import api from "src/util/api";

const P01_05_CreateUser: FC = () => {
  const dispatch = useAppDispatch();
  const rIsMobile = useSelector(
    (state) => state.NavigationReducer.deviceSize === "xs" || state.NavigationReducer.deviceSize === "sm",
  );
  const [sNewPassword, setNewPassword] = useState<string>("");
  const [sConfirmPassword, setConfirmPassword] = useState<string>("");
  const searchParams = new URLSearchParams(window.location.search);
  const kUserEmail = (searchParams.get("email") ?? "").replace("%40", "@");

  // FIXME: This should be in a thunk action
  const updateUserAndLogIn = (): void => {
    if (sNewPassword !== sConfirmPassword) {
      dispatch({
        type: "RIBBON",
        message: new Error("Passwords do not match"),
      });
      return undefined;
    }
    if (!validatePassword(sNewPassword)) {
      dispatch({
        type: "RIBBON",
        message: new Error(
          "Password must be at least 8 characters, and contain at least one capital letter, number, and a special character.",
        ),
      });
      return undefined;
    }
    if (searchParams.get("oobCode") === null || kUserEmail === null) {
      dispatch({
        type: "RIBBON",
        message: new Error("Something went wrong. Please close this window and follow the link sent to your email."),
      });
      return undefined;
    }
    api
      .postNoAuth({ endpoint: "/users/verify", body: { email: kUserEmail, password: sNewPassword } })
      .then(() => {
        dispatch({
          type: "RIBBON",
          message: "Password successfully changed.",
        });
        dispatch(loginWithEmailAndPassword(kUserEmail, sNewPassword));
      })
      .catch((e: Error) => {
        dispatch({
          type: "RIBBON",
          message: e,
        });
      });
    return undefined;
  };

  if (searchParams.get("mode") !== "verifyEmail") {
    return (
      <View>
        <Text>Close this window and try again.</Text>
      </View>
    );
  }

  return (
    <ScrollView
      contentContainerStyle={[styles.container, rIsMobile ? styles.m5 : styles.m10]}
      style={styles.bgBrandLight}
    >
      <View style={rIsMobile ? styles.wFull : styles.minWContainer}>
        <Text style={[styles.h2, styles.textAlignCenter]}>Create Password</Text>
        <Text style={[styles.body, styles.textAlignCenter, styles.mb10]}>
          Must include at least 8 characters, 1 capital letter, and 1 special character.
        </Text>
        <View style={styles.mb10}>
          <Text style={[styles.h3, styles.mb1]}>Email address</Text>
          <Text style={styles.readOnlyInputLogin}>{kUserEmail}</Text>
        </View>
        <View style={styles.mb10}>
          <Text style={[styles.h3, styles.mb1]}>Create Password</Text>
          <TextInput
            placeholder="Create User Password"
            autoCompleteType="password"
            onChangeText={setNewPassword}
            secureTextEntry={true}
            style={styles.inputLogin}
            placeholderTextColor={kColors.gray400}
          />
        </View>
        <View style={styles.mb10}>
          <Text style={[styles.h3, styles.mb1]}>Confirm Password</Text>
          <TextInput
            placeholder="Confirm User Password"
            autoCompleteType="password"
            onChangeText={setConfirmPassword}
            secureTextEntry={true}
            style={styles.inputLogin}
            placeholderTextColor={kColors.gray400}
          />
        </View>
        <PdButton onPress={updateUserAndLogIn} size="lg" isFullWidth={rIsMobile} style={rIsMobile ? styles.mb5 : {}}>
          <PdButtonLabel>Create Password</PdButtonLabel>
        </PdButton>
      </View>
    </ScrollView>
  );
};

export const P01_05 = React.memo(P01_05_CreateUser);

const styles = StyleSheet.create({
  bgBrandLight: {
    backgroundColor: kColors.brandLight,
  },
  body: {
    ...kFonts.poppinsRegularLight,
    color: kColors.brand500,
    letterSpacing: 1,
    lineHeight: 25,
  },
  container: {
    alignItems: "center",
    backgroundColor: kColors.white,
    borderRadius: 4 * kStyleConst.kBorderRadius,
    flexGrow: 1,
    justifyContent: "center",
    padding: 10 * kStyleConst.kSpacing,
  },
  h2: {
    ...kFonts.ralewayLargeLight,
    color: kColors.brand500,
    letterSpacing: 1.1,
    lineHeight: 35,
  },
  h3: {
    ...kFonts.H3,
    color: kColors.darkGray,
    letterSpacing: 1.1,
    lineHeight: 30,
  },
  inputLogin: {
    borderBottomWidth: 1,
    borderColor: kColors.brandPink,
    flex: 1,
    height: 36,
    outlineColor: kColors.transparent,
    ...kFonts.poppinsRegularLight,
    letterSpacing: 1,
    lineHeight: 25,
  },
  m10: {
    margin: 10 * kStyleConst.kSpacing,
  },
  m5: {
    margin: 5 * kStyleConst.kSpacing,
  },
  mb1: {
    marginBottom: 1 * kStyleConst.kSpacing,
  },
  mb10: {
    marginBottom: 10 * kStyleConst.kSpacing,
  },
  mb5: {
    marginBottom: 5 * kStyleConst.kSpacing,
  },
  minWContainer: {
    minWidth: 600,
  },
  readOnlyInputLogin: {
    flex: 1,
    height: 36,
    outlineColor: kColors.transparent,
    width: "100%",
    ...kFonts.poppinsRegularLight,
    letterSpacing: 1,
    lineHeight: 25,
  },
  textAlignCenter: {
    textAlign: "center",
  },
  wFull: {
    width: "100%",
  },
});
